import React from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import alephLogo from '../assets/logos/Aleph-Logomark-Blue.png';
import alephLogoWhite from '../assets/logos/Aleph-Logomark-White.png';

interface SplashScreenProps {
  isLoading: boolean;
  isAuthenticated: boolean;
  onNavigateToLogin?: (source?: string) => void;
  onNavigateToSignup?: (source?: string) => void;
}

/**
 * The native app's entry screen. It replaces the marketing HomePage inside the
 * Capacitor shell (App Store guideline 4.2 — an app, not a repackaged site).
 *
 * Logged out: branded splash with Log in / Sign up. While auth resolves, or when
 * already signed in (about to be routed into the app by the home-view effect in
 * app.tsx), it shows a quiet spinner instead of the CTAs so no marketing content
 * ever flashes.
 */
const SplashScreen: React.FC<SplashScreenProps> = ({
  isLoading,
  isAuthenticated,
  onNavigateToLogin,
  onNavigateToSignup,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const showAuthActions = !isLoading && !isAuthenticated;

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center px-8 pt-safe pb-safe"
      style={{ backgroundColor: isDarkMode ? 'var(--background-colour-dark-mode)' : 'var(--background-colour-light-mode)' }}
    >
      <div className="flex flex-col items-center">
        <img
          src={isDarkMode ? alephLogoWhite : alephLogo}
          alt="Aleph"
          className="w-24 h-24 object-contain mb-6"
        />
        <h1 className={`text-4xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-blue-700'}`}>Aleph</h1>
        <p className={`text-center text-base mb-12 ${isDarkMode ? 'text-gray-300' : 'text-blue-700'}`}>
          The literary companion in your pocket.
        </p>
      </div>

      {showAuthActions ? (
        <div className="w-full max-w-xs space-y-3">
          <button
            onClick={() => onNavigateToSignup?.('splash')}
            className={`w-full py-3 rounded-full text-base font-medium border transition-colors cursor-pointer ${
              isDarkMode
                ? 'border-blue-400 bg-blue-500 text-white hover:bg-blue-600'
                : 'border-blue-700 bg-blue-700 text-white hover:bg-blue-800'
            }`}
          >
            Sign up
          </button>
          <button
            onClick={() => onNavigateToLogin?.('splash')}
            className={`w-full py-3 rounded-full text-base font-medium border transition-colors cursor-pointer ${
              isDarkMode
                ? 'border-blue-400 text-blue-400 hover:bg-blue-900/20'
                : 'border-blue-600 text-blue-600 hover:bg-blue-50'
            }`}
          >
            Log in
          </button>
        </div>
      ) : (
        <span
          className={`inline-block w-6 h-6 border-2 rounded-full animate-spin ${
            isDarkMode ? 'border-gray-600 border-t-white' : 'border-blue-200 border-t-blue-700'
          }`}
          aria-label="Loading"
        />
      )}
    </div>
  );
};

export default SplashScreen;
